import { ClassicEditor } from 'ckeditor5'
import { useNotification } from 'components/WorkspaceEditor/services/notificationService'
import { MutableRefObject, useCallback, useRef, useState } from 'react'

/** Represents the state for an optimistic update. */
export interface OptimisticState<T> {
    /** The optimistic state data. */
    data: T
    /** The error object thrown during the update */
    error?: unknown
}

/** Parameters for the optimistic update.*/
interface RunOptimisticUpdateParams<T> {
    /** The fallback state to apply */
    fallbackState: T
    /** The async action to execute */
    asyncAction: () => Promise<any>
    /** Optional callback function to execute on success of asyncAction */
    successCbFn?: () => void
    /** Optional callback function to execute on failure of asyncAction */
    errorCbFn?: () => void
    /** Optional custom success message to display (defaults to "Changes Saved Successfully") */
    successMessage?: string
}
/** Type of handler to update state optimistically */
export type UpdateFunction<T> = (
    params: RunOptimisticUpdateParams<T>
) => Promise<void>

/** Type of result returned by useOptimistic */
export type OptimisticResult<T> = [
    OptimisticState<T>,
    UpdateFunction<T>,
    /** Reference to the CKEditor instance */
    MutableRefObject<ClassicEditor | undefined>
]

/**
 * A custom hook designed to handle optimistic updates,
 * specifically tailored for CKEditor interactions. It provides mechanism for:
 *
 * 1. Optimistic State Management:
 *    - immediately update UI with expected changes
 *    - track potential errors
 *    - revert changes if server-side operation fails
 * 2. Async Action Handling:
 *    - execute async mutations
 *    - manage loading/success/error states
 *    - provide automatic error notifications
 * 3. Editor State Preservation:
 *    - capture initial editor state (fallback state)
 *    - ability to revert to previous state on failure
 * 4. Callbacks:
 *    - optional success and error callbacks
 *
 * Key Components:
 *    - `optimisticState`: Tracks current state and potential errors
 *    - `runOptimisticUpdate`: Core method to execute optimistic updates
 *    - `revertChanges`: Rollback mechanism on update failure
 *    - `handleSuccessUpdate`: Process successful updates
 *
 * @template T - The type of the state being optimistically updated.
 * @param {ReturnType<typeof useNotification>} notificationService - Optional notification service for displaying update status.
 *
 * @returns {OptimisticResult<T>} A tuple containing:
 *   - `optimisticState`: Current state with potential error tracking
 *   - `runOptimisticUpdate`: Function to trigger optimistic updates
 *   - `editorRef`: Reference to the CKEditor instance
 *
 * !TODO: replace with useOptimistic hook from React v19 when available
 * @example
 * ```typescript
 *     // init optional notification service to custom hook
 *     const notification = useNotification()
 *
 *     // init hook that handles optimistic updates
 *     // destructure: [state, executor, CKEditor ref]
 *     // where:
 *     // - `optimisticState` is the fallback state when update fails
 *     // - `runOptimisticUpdate` is the function to trigger optimistic updates
 *     // - `editorRef` is the reference to the CKEditor instance
 *     const [optimisticState, runOptimisticUpdate, editorRef] = useOptimistic(notification)
 *
 *     // trigger optimistic update
 *     // where:
 *     // - `fallbackState` is the fallback state to apply
 *     // - `asyncAction` is the async action to execute
 *     // - `successCbFn` is the optional success callback
 *     // - `errorCbFn` is the optional error callback
 *     runOptimisticUpdate({
 *         fallbackState,
 *         asyncAction: () => mutateContentElementsAsync(contentItemId, suggestions),
 *         successCbFn: () => {
 *             // Optional success callback
 *         },
 *         errorCbFn: () => {
 *             // Optional error callback
 *         },
 *     })
 *
 * // if update fails, show/log notification and
 * // revert changes to CKE (editorRef) using fallback state
 * ```
 *
 * @throws {Error} When async action fails, automatically reverts changes
 */
export const useOptimistic = <T>(
    notificationService?: ReturnType<typeof useNotification>
): OptimisticResult<T> => {
    const editorRef = useRef<ClassicEditor>()
    /**
     * Ref to the fallback state.
     * This is used to revert the editor content to the previous state.
     */
    const fallbackStateRef = useRef<T>()
    const [optimisticState, setOptimisticState] = useState<OptimisticState<T>>({
        data: fallbackStateRef.current as T,
    })

    /** Reverts the editor content to the previous state and updates the optimistic state. */
    const onError = useCallback(
        (error: unknown, cbFn?: () => void) => {
            // Set editor content to initial state
            editorRef?.current?.setData(fallbackStateRef.current as any)
            // Revert to current actual state on error
            setOptimisticState({
                data: fallbackStateRef.current as T,
                error,
            })
            cbFn?.()

            console.error(error)
            notificationService?.error({
                message: 'There was a problem saving your changes.',
            })
        },
        [optimisticState?.data, fallbackStateRef, notificationService]
    )

    /** Extracts error message from GraphQL result and throws to revert optimistic changes */
    const parseError = useCallback((result: any) => {
        if (result instanceof Error) {
            return result.message
        }

        return (
            result?.errors?.networkError?.result?.errors?.[0]?.message ??
            result?.errors?.message ??
            'Unknown Error'
        )
    }, [])

    /**
     * Handles successful updates by updating the optimistic state and showing a success notification.
     * @param data - The response directly from asyncAction
     * @param cbFn - Optional callback function to execute on success
     * @param successMessage - Optional custom success message
     * */
    const onSuccess = useCallback(
        (data: T, cbFn?: () => void, successMessage?: string) => {
            // reflect the optimistic state (optional)
            setOptimisticState({ data })
            cbFn?.()

            notificationService?.success({
                message: successMessage || 'Changes Saved Successfully',
            })
        },
        [notificationService]
    )

    /** Executes an async action (mutation) and updates the optimistic state.
     * - if success, updates the optimistic state and shows a success notification.
     * - if error, reverts the editor content and shows an error notification.
     * @param fallbackState - The fallback state to apply.
     * @param asyncAction - The async action to execute.
     * @param successCbFn - Optional callback function to execute on success.
     * @param errorCbFn - Optional callback function to execute on failure.
     * @param successMessage - Optional custom success message.
     */
    const runOptimisticUpdate: UpdateFunction<T> = useCallback(
        async ({ fallbackState, asyncAction, successCbFn, errorCbFn, successMessage }) => {
            notificationService?.loading({ message: 'Saving...' })
            // capture fallbackState before running async action
            fallbackStateRef.current = fallbackState as T

            try {
                const result = await asyncAction()

                if (result?.errors) {
                    // throw error to revert optimistic changes
                    onError(parseError(result), errorCbFn)
                    return
                }

                onSuccess(result, successCbFn, successMessage)
            } catch (error) {
                onError(error, errorCbFn)
            }
        },
        [onSuccess, onError, notificationService]
    )

    return [optimisticState, runOptimisticUpdate, editorRef]
}
